import React from 'react';
import { Database, Cpu, Lock, Zap, Network, Brain, Scan } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { useLanguage } from '../contexts/LanguageContext';

export const Technology = () => {
  const { t } = useLanguage();
  
  const techStack = [
    {
      icon: Brain,
      title: t('technology.cards.gnn.title'),
      description: t('technology.cards.gnn.description'),
      features: [t('technology.cards.gnn.features.0'), t('technology.cards.gnn.features.1'), t('technology.cards.gnn.features.2')],
    },
    {
      icon: Database,
      title: t('technology.cards.selfOptimizing.title'),
      description: t('technology.cards.selfOptimizing.description'),
      features: [t('technology.cards.selfOptimizing.features.0'), t('technology.cards.selfOptimizing.features.1'), t('technology.cards.selfOptimizing.features.2')],
    },
    {
      icon: Cpu,
      title: t('technology.cards.multiAgent.title'),
      description: t('technology.cards.multiAgent.description'),
      features: [t('technology.cards.multiAgent.features.0'), t('technology.cards.multiAgent.features.1'), t('technology.cards.multiAgent.features.2')],
    },
    {
      icon: Lock,
      title: t('technology.cards.zeroTrust.title'),
      description: t('technology.cards.zeroTrust.description'),
      features: [t('technology.cards.zeroTrust.features.0'), t('technology.cards.zeroTrust.features.1'), t('technology.cards.zeroTrust.features.2')],
    },
    {
      icon: Network,
      title: t('technology.cards.localEmbeddings.title'),
      description: t('technology.cards.localEmbeddings.description'),
      features: [t('technology.cards.localEmbeddings.features.0'), t('technology.cards.localEmbeddings.features.1'), t('technology.cards.localEmbeddings.features.2')],
    },
    {
      icon: Zap,
      title: t('technology.cards.orchestration.title'),
      description: t('technology.cards.orchestration.description'),
      features: [t('technology.cards.orchestration.features.0'), t('technology.cards.orchestration.features.1'), t('technology.cards.orchestration.features.2')],
    },
    {
      icon: Scan,
      title: t('technology.cards.quantx.title'),
      description: t('technology.cards.quantx.description'),
      features: [t('technology.cards.quantx.features.0'), t('technology.cards.quantx.features.1'), t('technology.cards.quantx.features.2')],
    },
  ];

  return (
    <section id="technology" className="py-20 sm:py-32 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 bg-gradient-subtle" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="outline" className="glass border-primary/30 mb-4">
            <Cpu className="w-4 h-4 mr-2 inline" />
            {t('technology.title')}
          </Badge>
          <h2 className="font-serif font-bold mb-6 text-gradient-primary">
            {t('technology.subtitle')}
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            {t('technology.description')}
          </p>
        </div>

        {/* Technology Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {techStack.map((tech, index) => {
            const Icon = tech.icon;
            return (
              <Card
                key={index}
                className="glass border-card-border hover-lift group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-xl bg-gradient-primary/10 border border-primary/20 group-hover:glow-teal transition-all duration-300">
                      <Icon className="w-6 h-6 text-primary-glow" />
                    </div>
                  </div>
                  <CardTitle className="text-xl font-serif text-foreground">{tech.title}</CardTitle>
                  <CardDescription className="text-muted-foreground leading-relaxed">
                    {tech.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {tech.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-secondary" />
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom Stats */}
        <div className="mt-16 glass rounded-2xl p-8 border border-card-border">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-serif font-semibold text-gradient-gold mb-2">{t('technology.stats.decentralized.value')}</div>
              <div className="text-sm text-muted-foreground">{t('technology.stats.decentralized.label')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-serif font-semibold text-gradient-gold mb-2">{t('technology.stats.compression.value')}</div>
              <div className="text-sm text-muted-foreground">{t('technology.stats.compression.label')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-serif font-semibold text-gradient-gold mb-2">{t('technology.stats.dimensions.value')}</div>
              <div className="text-sm text-muted-foreground">{t('technology.stats.dimensions.label')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-serif font-semibold text-gradient-gold mb-2">{t('technology.stats.cloud.value')}</div>
              <div className="text-sm text-muted-foreground">{t('technology.stats.cloud.label')}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};